import React, { Suspense, useState, useEffect } from "react";
import { Loader2, RotateCw, FlipHorizontal, AlignCenter, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import ShirtDesigner2D from "./ShirtDesigner2D";
import DesignContextMenu from "./DesignContextMenu";
import { useToast } from "@/hooks/use-toast";

interface TShirtCanvasProps {
  color: string;
  designUrl: string | null;
  customModelUrl?: string | null;
  rotation?: boolean;
  designSize?: number;
  showBackDesign?: boolean;
  backDesignUrl?: string | null;
  isMobile?: boolean;
  designPosition?: { x: number, y: number } | null;
  designRotation?: number;
  isAdjusting?: boolean;
  onDesignClick?: () => void;
  onDesignRemove?: () => void;
  onPositionChange?: (position: { x: number, y: number }) => void;
}

const TShirtCanvas = ({ 
  color, 
  designUrl, 
  customModelUrl = null,
  rotation = true,
  designSize = 1,
  showBackDesign = false,
  backDesignUrl = null,
  designPosition = { x: 50, y: 40 },
  designRotation = 0,
  isAdjusting = false,
  onDesignClick,
  onDesignRemove,
  onPositionChange
}: TShirtCanvasProps) => {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [canvasError, setCanvasError] = useState<string | null>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(designPosition);
  
  useEffect(() => {
    setAdjustedPosition(designPosition || { x: 50, y: 40 });
  }, [designPosition]);
  
  const frontShirtImage = "/lovable-uploads/b85a05cf-0a3a-4233-9cb9-1aeb27926a34.png";
  const backShirtImage = "/lovable-uploads/d0288ac4-d8b9-4881-924e-c5a652a5b872.png";

  const handleRotate = () => {
    if (onDesignClick) {
      onDesignClick();
      setTimeout(() => {
        const rotateEvent = new CustomEvent('design-rotate');
        document.dispatchEvent(rotateEvent);
      }, 100);
    }
  };

  const handleFlip = () => {
    if (onDesignClick) {
      onDesignClick();
      setTimeout(() => {
        const flipEvent = new CustomEvent('design-flip');
        document.dispatchEvent(flipEvent);
      }, 100);
    }
  };

  const handleCenter = () => {
    if (onDesignClick) {
      onDesignClick();
      setTimeout(() => {
        const centerEvent = new CustomEvent('design-center');
        document.dispatchEvent(centerEvent);
      }, 100);
    }
  };
  
  const handleShirtDesignerPositionChange = (newPosition: { x: number, y: number }) => {
    setAdjustedPosition(newPosition);
    if (onPositionChange) {
      onPositionChange(newPosition);
    }
  };

  return (
    <div 
      className="relative w-full h-full rounded-lg overflow-hidden bg-gradient-to-b from-gray-50 to-gray-100"
      onClick={isAdjusting ? undefined : onDesignClick}
    >
      {canvasError ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-red-500">{canvasError}</p>
        </div>
      ) : (
        <Suspense fallback={
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="sr-only md:not-sr-only md:ml-2">Loading design...</span>
          </div>
        }>
          {designUrl ? (
            <DesignContextMenu
              onRotate={handleRotate}
              onFlip={handleFlip}
              onCenter={handleCenter}
              onRemove={onDesignRemove || (() => {})}
            >
              <div className="w-full h-full">
                <ShirtDesigner2D
                  color={color}
                  designUrl={designUrl}
                  showBackDesign={showBackDesign}
                  designSize={designSize}
                  designPosition={adjustedPosition}
                  designRotation={designRotation}
                  frontImageUrl={frontShirtImage}
                  backImageUrl={backShirtImage}
                  isAdjusting={isAdjusting}
                  onPositionChange={handleShirtDesignerPositionChange}
                />
              </div>
            </DesignContextMenu>
          ) : (
            <ShirtDesigner2D
              color={color}
              designUrl={designUrl}
              showBackDesign={showBackDesign}
              designSize={designSize}
              designPosition={adjustedPosition}
              designRotation={designRotation}
              frontImageUrl={frontShirtImage}
              backImageUrl={backShirtImage}
              isAdjusting={isAdjusting}
              onPositionChange={handleShirtDesignerPositionChange}
            />
          )}
        </Suspense>
      )}
    </div>
  );
};

export default TShirtCanvas;
